// Title-screen backdrop: a slow, low-poly "Oil Creek at dusk" diorama on a 2D canvas.
// Flat-shaded faceted hills (DESIGN §16 low-poly look), silhouetted cable-tool derricks with rocking walking
// beams, a distant gusher against the sun, drifting engine smoke, lamp light, stars, stratus and birds.
// createDuskScene(canvas, { reducedMotion }) -> { start(), stop(), resize(), draw(t), setMood(m), dispose() }
// Pure canvas — no DOM besides the canvas it is given. Deterministic layout (seeded), animated details only.

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
function hexRgb(h) { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
function mix(c1, c2, t) { const a = hexRgb(c1), b = hexRgb(c2); return `rgb(${Math.round(lerp(a[0], b[0], t))},${Math.round(lerp(a[1], b[1], t))},${Math.round(lerp(a[2], b[2], t))})`; }

const SKY = [
  [0.00, '#0b0a13'], [0.22, '#171427'], [0.40, '#2e2036'], [0.53, '#5a2c33'],
  [0.61, '#9a4a2c'], [0.655, '#d9772e'], [0.67, '#f2a93b'],
];
// back -> front: [ridge colour (shadow side), lit colour, haze toward sky]
const LAYERS = [
  { y: 0.585, amp: 0.05, step: 0.07, dark: '#4b2832', lit: '#8a4636', depth: 22 },
  { y: 0.655, amp: 0.055, step: 0.085, dark: '#2b1820', lit: '#5a2c28', depth: 28 },
  { y: 0.745, amp: 0.06, step: 0.11, dark: '#170e12', lit: '#33191a', depth: 36 },
  { y: 0.86, amp: 0.035, step: 0.16, dark: '#0b0708', lit: '#1a0f0e', depth: 40 },
];

export function createDuskScene(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  let W = 0, H = 0, dpr = 1, raf = 0, running = false, lastT = 0, mood = 'title';
  let staticLayer = null; // offscreen: sky + stars + hills (everything that doesn't move)
  let rigs = [], smoke = [], oil = [], birds = [], clouds = [], stars = [];
  const reduced = !!opts.reducedMotion;
  const sunX = () => W * 0.7, sunY = () => H * 0.548, horizon = () => H * 0.62;

  function build() {
    const rnd = mulberry32(1859);
    // stars
    stars = Array.from({ length: Math.round((W * H) / 9000) }, () => ({ x: rnd() * W, y: Math.pow(rnd(), 1.7) * H * 0.5, r: rnd() * 1.1 + 0.25, tw: rnd() * 6.28, sp: 0.4 + rnd() * 1.4 }));
    // ridges
    for (const L of LAYERS) {
      const pts = [];
      const step = W * L.step;
      for (let x = -step; x <= W + step * 1.5; x += step * (0.7 + rnd() * 0.6)) {
        const n = Math.sin(x / W * 5.1 + L.y * 9) * 0.55 + Math.sin(x / W * 13.7 + L.y * 3) * 0.3 + (rnd() - 0.5) * 0.5;
        pts.push([x, H * L.y - n * H * L.amp - H * L.amp * 0.4]);
      }
      L.pts = pts;
      L.sub = pts.slice(0, -1).map((p, i) => [(p[0] + pts[i + 1][0]) / 2 + (rnd() - 0.5) * step * 0.3, Math.max(p[1], pts[i + 1][1]) + L.depth * (H / 900) * (0.7 + rnd() * 0.7)]);
    }
    // rigs: [layer, x fraction, height fraction, kind]
    const R = [
      [0, 0.585, 0.07, 'far'], [0, 0.63, 0.06, 'far'], [0, 0.79, 0.075, 'far'], [0, 0.86, 0.055, 'far'], [0, 0.44, 0.06, 'far'], [0, 0.94, 0.065, 'far'],
      [1, 0.655, 0.12, 'gusher'], [1, 0.52, 0.1, 'mid'], [1, 0.9, 0.11, 'mid'], [1, 0.38, 0.09, 'mid'],
      [2, 0.78, 0.2, 'near'], [2, 0.6, 0.17, 'near'], [2, 0.97, 0.18, 'near'],
    ];
    rigs = R.map(([li, fx, fh, kind]) => {
      const L = LAYERS[li];
      const x = W * fx;
      return { li, kind, x, base: ridgeY(L, x) + 2, h: H * fh * (W < 700 ? 0.8 : 1), phase: rnd() * 6.28, speed: 0.55 + rnd() * 0.35, lamp: kind === 'near' || kind === 'mid', smokeAcc: rnd() };
    });
    clouds = Array.from({ length: 6 }, () => {
      const w = W * (0.14 + rnd() * 0.22), h = H * (0.016 + rnd() * 0.02), n = 9 + Math.floor(rnd() * 4);
      const top = [], mid = [], bot = [];
      const skew = 0.3 + rnd() * 0.4; // tallest bump off-centre
      for (let k = 0; k <= n; k++) {
        const f = k / n;
        const env = f < skew ? (f / skew) ** 0.8 : ((1 - f) / (1 - skew)) ** 1.4;
        const bump = 0.35 + rnd() * 0.75;
        top.push([w * f, -h * env * bump * 1.2]);
        mid.push([w * f + (rnd() - 0.5) * w / n * 0.5, -h * env * 0.22]);
        bot.push([w * f, h * 0.12 * Math.min(1, env * 2)]);
      }
      return { x: rnd() * W * 1.3 - W * 0.15, y: H * (0.15 + rnd() * 0.3), w, h, top, mid, bot, sp: 3 + rnd() * 5 };
    });
    birds = Array.from({ length: 4 }, (_, i) => ({ x: rnd() * W, y: H * (0.3 + rnd() * 0.15), sp: 14 + rnd() * 10, ph: rnd() * 6, s: 3 + rnd() * 2.5 + i * 0.3 }));
    smoke = []; oil = [];
    bakeStatic();
    // pre-warm particles so the first frame isn't empty
    if (!reduced) for (let i = 0; i < 240; i++) step(1 / 15, i / 15);
    else for (let i = 0; i < 300; i++) step(1 / 15, i / 15);
  }

  function ridgeY(L, x) {
    const p = L.pts;
    for (let i = 0; i < p.length - 1; i++) if (x >= p[i][0] && x <= p[i + 1][0]) return lerp(p[i][1], p[i + 1][1], (x - p[i][0]) / (p[i + 1][0] - p[i][0]));
    return H * L.y;
  }

  function bakeStatic() {
    staticLayer = document.createElement('canvas');
    staticLayer.width = Math.round(W * dpr); staticLayer.height = Math.round(H * dpr);
    const g = staticLayer.getContext('2d');
    g.scale(dpr, dpr);
    // sky
    const sky = g.createLinearGradient(0, 0, 0, horizon());
    for (const [o, c] of SKY) sky.addColorStop(Math.min(1, o / 0.67), c);
    g.fillStyle = sky; g.fillRect(0, 0, W, H);
    // sun glow
    const sx = sunX(), sy = sunY();
    const glow = g.createRadialGradient(sx, sy, 0, sx, sy, Math.max(W, H) * 0.55);
    glow.addColorStop(0, 'rgba(255,214,140,.85)'); glow.addColorStop(0.05, 'rgba(255,170,80,.55)');
    glow.addColorStop(0.22, 'rgba(225,110,50,.22)'); glow.addColorStop(0.55, 'rgba(120,50,60,.08)'); glow.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = glow; g.fillRect(0, 0, W, H);
    // sun disc, sinking behind the far ridge (hills are painted over it)
    const sr = Math.min(W, H) * 0.038;
    const disc = g.createLinearGradient(0, sy - sr, 0, sy + sr);
    disc.addColorStop(0, '#fff1c4'); disc.addColorStop(1, '#ffb455');
    g.fillStyle = disc; g.beginPath(); g.arc(sx, sy + sr * 0.35, sr, 0, Math.PI * 2); g.fill();
    // hills, back to front, faceted
    LAYERS.forEach((L, li) => drawRidge(g, L, li));
  }

  function drawRidge(g, L, li) {
    const p = L.pts, s = L.sub;
    const sx = sunX();
    // solid body
    g.fillStyle = L.dark;
    g.beginPath(); g.moveTo(p[0][0], H); for (const q of p) g.lineTo(q[0], q[1]); g.lineTo(p.at(-1)[0], H); g.closePath(); g.fill();
    // facets: ridge row p, sub row s
    const tri = (a, b, c, extra = 0) => {
      // pseudo-3D normal: ridge points sit back (z=0), sub points forward (z=1)
      const A = [a[0], -a[1], a[2]], B = [b[0], -b[1], b[2]], C = [c[0], -c[1], c[2]];
      const u = [B[0] - A[0], B[1] - A[1], (B[2] - A[2]) * 40], v = [C[0] - A[0], C[1] - A[1], (C[2] - A[2]) * 40];
      let n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
      const len = Math.hypot(...n) || 1; n = n.map((x) => x / len); if (n[2] < 0) n = n.map((x) => -x);
      const cx = (a[0] + b[0] + c[0]) / 3;
      const toward = clamp01(1 - Math.abs(cx - sx) / (W * 0.9));
      const L3 = [Math.sign(sx - cx) * 0.75, 0.45, 0.5];
      const lit = clamp01((n[0] * L3[0] + n[1] * L3[1] + n[2] * L3[2]) * 0.9 + 0.05 + extra) * (0.45 + 0.55 * toward);
      g.fillStyle = mix(L.dark, L.lit, lit);
      g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.lineTo(c[0], c[1]); g.closePath(); g.fill();
      g.strokeStyle = g.fillStyle; g.lineWidth = 0.6; g.stroke();
    };
    for (let i = 0; i < s.length; i++) {
      const a = [...p[i], 0], b = [...p[i + 1], 0], m = [...s[i], 1];
      tri(a, b, m);
      if (i + 1 < s.length) tri(b, m, [...s[i + 1], 1], -0.1);
    }
    // haze toward the horizon glow on far layers
    if (li < 2) {
      const top = Math.min(...p.map((q) => q[1]));
      const hz = g.createLinearGradient(0, top, 0, H * (L.y + 0.08));
      hz.addColorStop(0, `rgba(242,140,70,${li === 0 ? 0.18 : 0.08})`); hz.addColorStop(1, 'rgba(242,140,70,0)');
      g.fillStyle = hz; g.beginPath(); g.moveTo(p[0][0], H); for (const q of p) g.lineTo(q[0], q[1]); g.lineTo(p.at(-1)[0], H); g.closePath(); g.fill();
    }
    // rim light along the crest facing the sun
    g.lineWidth = li === 0 ? 1 : 1.4;
    for (let i = 0; i < p.length - 1; i++) {
      const cx = (p[i][0] + p[i + 1][0]) / 2;
      const facing = (p[i + 1][1] - p[i][1]) * Math.sign(sx - cx) < 0;
      const near = clamp01(1 - Math.abs(cx - sx) / (W * 0.6));
      if (!facing || near <= 0) continue;
      g.strokeStyle = `rgba(255,190,110,${0.32 * near * (li === 0 ? 0.8 : 1 - li * 0.22)})`;
      g.beginPath(); g.moveTo(p[i][0], p[i][1]); g.lineTo(p[i + 1][0], p[i + 1][1]); g.stroke();
    }
  }

  // ---------- dynamic ----------
  function step(dt, t) {
    const wind = W * 0.012;
    for (const r of rigs) {
      if (r.kind === 'far') continue;
      r.smokeAcc += dt * (r.kind === 'near' ? 2.4 : 1.4);
      while (r.smokeAcc > 1) {
        r.smokeAcc -= 1;
        const st = stackPos(r);
        smoke.push({ x: st[0], y: st[1], vx: wind * (0.6 + Math.random() * 0.5), vy: -r.h * (0.07 + Math.random() * 0.04), r: r.h * 0.03, g: r.h * 0.035, life: 0, max: 9 + Math.random() * 5, li: r.li });
      }
      if (r.kind === 'gusher') {
        for (let k = 0; k < Math.round(dt * 420); k++) {
          const a = -Math.PI / 2 + (Math.random() - 0.5) * 0.9;
          const sp = r.h * (0.3 + Math.random() * 0.45);
          oil.push({ x: r.x + (Math.random() - 0.5) * r.h * 0.1, y: r.base - r.h * 1.4, vx: Math.cos(a) * sp + wind * 0.4, vy: Math.sin(a) * sp, life: 0, r: 0.6 + Math.random() * 1.4, base: r.base });
        }
      }
    }
    for (const s of smoke) { s.life += dt; s.x += s.vx * dt; s.y += s.vy * dt; s.vy *= 0.995; s.vx += wind * 0.02 * dt; s.r += s.g * dt; }
    smoke = smoke.filter((s) => s.life < s.max && s.x < W + 200);
    const grav = H * 0.22;
    for (const o of oil) { o.life += dt; o.x += o.vx * dt; o.y += o.vy * dt; o.vy += grav * dt; }
    oil = oil.filter((o) => o.y < o.base + 4 && o.life < 4);
    for (const c of clouds) { c.x += c.sp * dt; if (c.x > W + c.w * 0.6) c.x = -c.w * 1.2; }
    for (const b of birds) { b.x += b.sp * dt; if (b.x > W + 40) { b.x = -40; b.y = H * (0.28 + Math.random() * 0.15); } }
  }

  function stackPos(r) {
    const s = r.h / 100, dir = r.x > sunX() ? 1 : -1; // engine house on the side away from... keep variety
    return [r.x + dir * 34 * s, r.base - 30 * s];
  }

  function drawRig(g, r, t) {
    const { x, base, h } = r;
    const L = LAYERS[r.li];
    const col = r.li === 0 ? '#3a1f28' : r.li === 1 ? '#1d1016' : '#0c0709';
    g.fillStyle = col; g.strokeStyle = col;
    const bw = h * 0.21, tw = h * 0.045;
    const lw = Math.max(0.8, h * (r.kind === 'far' ? 0.014 : 0.018));
    g.lineWidth = lw; g.lineCap = 'round';
    // legs
    g.beginPath(); g.moveTo(x - bw, base); g.lineTo(x - tw, base - h); g.lineTo(x + tw, base - h); g.lineTo(x + bw, base); g.stroke();
    // braces
    const n = r.kind === 'far' ? 3 : 6;
    g.lineWidth = lw * 0.7;
    g.beginPath();
    for (let i = 0; i <= n; i++) {
      const f = i / n, y = base - h * f * 0.94, w = lerp(bw, tw, f * 0.94);
      g.moveTo(x - w, y); g.lineTo(x + w, y);
      if (i < n) { const f2 = (i + 1) / n, y2 = base - h * f2 * 0.94, w2 = lerp(bw, tw, f2 * 0.94); g.moveTo(x - w, y); g.lineTo(x + w2, y2); g.moveTo(x + w, y); g.lineTo(x - w2, y2); }
    }
    g.stroke();
    // crown block
    g.fillRect(x - tw * 1.6, base - h - h * 0.025, tw * 3.2, h * 0.03);
    if (r.kind === 'far') return;
    const s = h / 100, dir = x > sunX() ? 1 : -1;
    // derrick floor
    g.fillRect(x - bw * 1.15, base - 6 * s, bw * 2.3, 3 * s);
    // engine house + stack
    const ex = x + dir * 22 * s;
    g.beginPath();
    g.moveTo(ex - 9 * s, base); g.lineTo(ex - 9 * s, base - 12 * s); g.lineTo(ex, base - 18 * s); g.lineTo(ex + 9 * s, base - 12 * s); g.lineTo(ex + 9 * s, base);
    g.closePath(); g.fill();
    // engine-house rim light toward the sun
    g.fillRect(x + dir * 34 * s - 1.3 * s, base - 30 * s, 2.6 * s, 30 * s);
    // walking beam: samson post between derrick and engine house, beam rocks
    if (r.kind !== 'gusher') {
      const px = x + dir * 13 * s, py = base - 15 * s;
      g.lineWidth = 2 * s;
      g.beginPath(); g.moveTo(px - 2.5 * s, base); g.lineTo(px, py); g.lineTo(px + 2.5 * s, base); g.stroke();
      const ang = reduced ? 0.06 : Math.sin(t * r.speed * 2.2 + r.phase) * 0.12;
      const len = 13 * s;
      g.lineWidth = 2.6 * s;
      g.beginPath(); g.moveTo(px - Math.cos(ang) * len * dir, py - Math.sin(ang) * len); g.lineTo(px + Math.cos(ang) * len * 0.75 * dir, py + Math.sin(ang) * len * 0.75); g.stroke();
      // band wheel
      g.lineWidth = 1.2 * s;
      g.beginPath(); g.arc(ex - dir * 3 * s, base - 6 * s, 5 * s, 0, Math.PI * 2); g.stroke();
    }
    // lamp
    if (r.lamp) {
      const flick = reduced ? 1 : 0.82 + Math.sin(t * 7.3 + r.phase) * 0.08 + Math.sin(t * 13.1 + r.phase * 2) * 0.06;
      const lx = x - dir * bw * 0.4, ly = base - 9 * s;
      const rad = 26 * s * flick;
      const gl = g.createRadialGradient(lx, ly, 0, lx, ly, rad);
      gl.addColorStop(0, `rgba(255,200,110,${0.9 * flick})`); gl.addColorStop(0.15, `rgba(255,160,60,${0.4 * flick})`); gl.addColorStop(1, 'rgba(255,140,40,0)');
      g.fillStyle = gl; g.beginPath(); g.arc(lx, ly, rad, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#ffe0a0'; g.beginPath(); g.arc(lx, ly, Math.max(1, 1.2 * s), 0, Math.PI * 2); g.fill();
      // window glow in the engine house
      g.fillStyle = `rgba(255,170,80,${0.55 * flick})`; g.fillRect(ex - 2 * s, base - 9 * s, 4 * s, 3.5 * s);
    }
    void L;
  }

  function drawCloud(g, c) {
    const sx = sunX();
    const near = clamp01(1 - Math.abs(c.x + c.w / 2 - sx) / (W * 0.75));
    const yN = clamp01(c.y / sunY());
    const shade = `rgba(${Math.round(lerp(48, 96, yN))},${Math.round(lerp(34, 50, yN))},${Math.round(lerp(58, 62, yN))},.8)`;
    const shade2 = `rgba(${Math.round(lerp(62, 130, yN))},${Math.round(lerp(42, 62, yN))},${Math.round(lerp(66, 64, yN))},.8)`;
    const lit = `rgba(${Math.round(lerp(150, 255, near))},${Math.round(lerp(90, 170, near))},${Math.round(lerp(90, 110, near))},${(0.22 + 0.5 * near * near) * (0.4 + 0.6 * yN)})`;
    const P = (q) => [c.x + q[0], c.y + q[1]];
    const tri = (a, b, d, f) => { g.fillStyle = f; g.beginPath(); g.moveTo(...P(a)); g.lineTo(...P(b)); g.lineTo(...P(d)); g.closePath(); g.fill(); };
    for (let i = 0; i < c.top.length - 1; i++) {
      tri(c.top[i], c.top[i + 1], c.mid[i], i % 2 ? shade : shade2);
      tri(c.top[i + 1], c.mid[i], c.mid[i + 1], i % 2 ? shade2 : shade);
      tri(c.mid[i], c.mid[i + 1], c.bot[i], lit);
      tri(c.mid[i + 1], c.bot[i], c.bot[i + 1], lit);
    }
  }

  function draw(t = 0) {
    if (!W) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(staticLayer, 0, 0, W, H);
    // stars twinkle
    for (const s of stars) {
      const a = reduced ? 0.6 : 0.35 + 0.45 * (0.5 + 0.5 * Math.sin(t * s.sp + s.tw));
      ctx.fillStyle = `rgba(255,236,200,${a * (1 - s.y / (H * 0.5)) ** 1.2})`;
      ctx.fillRect(s.x, s.y, s.r, s.r);
    }
    for (const c of clouds) drawCloud(ctx, c);
    // birds
    ctx.strokeStyle = 'rgba(20,10,14,.75)'; ctx.lineWidth = 1.2;
    for (const b of birds) {
      const f = reduced ? 0.4 : Math.sin(t * 6 + b.ph) * 0.6;
      ctx.beginPath(); ctx.moveTo(b.x - b.s, b.y - f * b.s * 0.6); ctx.quadraticCurveTo(b.x - b.s * 0.4, b.y - b.s * 0.3, b.x, b.y); ctx.quadraticCurveTo(b.x + b.s * 0.4, b.y - b.s * 0.3, b.x + b.s, b.y - f * b.s * 0.6); ctx.stroke();
    }
    // rigs & particles are drawn per layer so nearer hills occlude farther rigs. Re-draw the nearer layers'
    // ridge bodies from the static bitmap by clipping: simplest is drawing rigs back-to-front with their own
    // layer's hills re-painted in front. We paint layer li rigs, then the static strip for layers > li.
    for (let li = 0; li < LAYERS.length; li++) {
      for (const s of smoke) if (s.li === li) drawSmoke(ctx, s);
      for (const r of rigs) if (r.li === li) drawRig(ctx, r, t);
      if (li === 1) drawOil(ctx, t);
      if (li + 1 < LAYERS.length) repaintFront(li + 1);
    }
    // grade
    const v = ctx.createRadialGradient(W * 0.62, H * 0.6, Math.min(W, H) * 0.2, W * 0.5, H * 0.55, Math.max(W, H) * 0.85);
    v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, mood === 'intro' ? 'rgba(4,2,2,.82)' : 'rgba(4,2,2,.55)');
    ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
    if (mood === 'intro') { ctx.fillStyle = 'rgba(6,3,4,.32)'; ctx.fillRect(0, 0, W, H); }
  }

  // Paint layers [from..end] from the static bitmap (clip to their silhouettes) so they cover farther rigs.
  const frontPaths = [];
  function repaintFront(from) {
    let p = frontPaths[from];
    if (!p) {
      p = new Path2D();
      const pts = LAYERS[from].pts;
      p.moveTo(pts[0][0], H + 1); for (const q of pts) p.lineTo(q[0], q[1]); p.lineTo(pts.at(-1)[0], H + 1); p.closePath();
      frontPaths[from] = p;
    }
    ctx.save(); ctx.clip(p);
    ctx.drawImage(staticLayer, 0, 0, W, H);
    ctx.restore();
  }

  function drawSmoke(g, s) {
    const k = s.life / s.max;
    const a = (k < 0.1 ? k / 0.1 : 1 - (k - 0.1) / 0.9) * (s.li === 2 ? 0.11 : 0.08);
    const nearSun = clamp01(1 - Math.abs(s.x - sunX()) / (W * 0.5));
    g.fillStyle = `rgba(${Math.round(lerp(80, 170, nearSun))},${Math.round(lerp(66, 104, nearSun))},${Math.round(lerp(78, 92, nearSun))},${a})`;
    g.beginPath();
    // hexagonal puffs keep the low-poly vocabulary
    for (let i = 0; i < 6; i++) { const an = (i / 6) * Math.PI * 2 + s.life * 0.2; const px = s.x + Math.cos(an) * s.r, py = s.y + Math.sin(an) * s.r; i ? g.lineTo(px, py) : g.moveTo(px, py); }
    g.closePath(); g.fill();
  }

  function drawOil(g, t) {
    const r = rigs.find((q) => q.kind === 'gusher');
    if (r) {
      const top = r.base - r.h, apex = top - r.h * 0.42, wob = reduced ? 0 : Math.sin(t * 3.1) * r.h * 0.01;
      g.fillStyle = 'rgba(18,9,7,.82)';
      g.beginPath();
      g.moveTo(r.x - r.h * 0.012, top); g.lineTo(r.x - r.h * 0.03 + wob, apex + r.h * 0.05);
      g.lineTo(r.x - r.h * 0.06 + wob, apex); g.lineTo(r.x + r.h * 0.07 + wob, apex - r.h * 0.01);
      g.lineTo(r.x + r.h * 0.035 + wob, apex + r.h * 0.05); g.lineTo(r.x + r.h * 0.012, top); g.closePath(); g.fill();
    }
    g.fillStyle = 'rgba(20,10,8,.5)';
    const k = H / 900;
    for (const o of oil) { const sz = o.r * k * (0.9 + o.life * 0.5); g.fillRect(o.x - sz / 2, o.y - sz / 2, sz, sz); }
  }

  function loop(now) {
    if (!running) return;
    raf = requestAnimationFrame(loop);
    const t = now / 1000;
    const dt = Math.min(0.1, t - (lastT || t));
    if (dt < 1 / 32 && lastT) return; // ~30 fps is plenty for a backdrop
    lastT = t;
    step(dt, t);
    draw(t);
  }

  function resize() {
    const r = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
    dpr = Math.min(2, window.devicePixelRatio || 1);
    if (w === W && h === H && canvas.width === Math.round(w * dpr)) return;
    W = w; H = h;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    frontPaths.length = 0;
    build();
    draw(performance.now() / 1000);
  }

  const onVis = () => { if (document.hidden) pause(); else if (wantRun) start(); };
  let wantRun = false;
  function start() { wantRun = true; if (reduced || running) return; running = true; lastT = 0; raf = requestAnimationFrame(loop); }
  function pause() { running = false; cancelAnimationFrame(raf); }
  function stop() { wantRun = false; pause(); }
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resize()) : null;
  ro?.observe(canvas);
  document.addEventListener('visibilitychange', onVis);
  resize();

  return {
    start, stop, resize, draw,
    setMood(m) { mood = m; draw(performance.now() / 1000); },
    dispose() { stop(); ro?.disconnect(); document.removeEventListener('visibilitychange', onVis); },
  };
}
